/**
 * Needlepoint charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const needlepointChartsPack: ToilePack = {
    slug: 'needlepoint-charts',
    category: 'craft',
    name: 'Needlepoint charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'cushion', label: 'Cushion', subject: 'a needlepoint chart for a square cushion front, a bunch of lemons with leaves on a navy ground and a narrow checked border, one square per tent stitch'},
        {key: 'bargello', label: 'Bargello', subject: 'a bargello needlepoint chart of a flame pattern, long vertical stitches climbing and falling in waves of five graded shades from cream to deep coral'},
        {key: 'keyfob', label: 'Key fob', subject: 'a small needlepoint chart for a rectangular key fob, a tennis racket and ball on a pale green ground, a plain border two stitches wide'},
    ],
    styles: [
        {key: 'colour', label: 'Colour chart', style: 'a clean colour chart, every square filled flat in its colour with fine light-gray grid lines between them', palette: 'a limited palette of the wool colours named', framing: 'the chart centred on a plain white sheet with an even margin'},
        {key: 'symbol', label: 'Symbol chart', style: 'a chart for black-and-white printing, every square carrying one small black symbol over a pale colour so each shade stays distinct', palette: 'pale colours kept under crisp black symbols', framing: 'the chart filling most of a white sheet with a narrow clean margin'},
        {key: 'graph', label: 'Graph paper', style: 'a hand-coloured worksheet, each square filled in with felt-tip pens, slightly uneven strokes and a few pencil guide marks, on blue squared graph paper', palette: 'bright felt-tip colours on pale blue grid paper', framing: 'the chart drawn on a plain sheet with a small margin all round'},
    ],
    directions: ['a dog', 'tulips', 'monogram'],
    directionHint: 'try: a dog',
    seo: {
        title: 'Needlepoint charts: cushion, bargello, key fob',
        metaDescription: 'Nine needlepoint charts: a lemon cushion, a bargello flame and a key fob, one square per stitch, in three styles.',
        h1: 'Needlepoint charts: a lemon cushion, a bargello flame and a key fob',
        tagline: 'Coloured canvas charts, one square per stitch, in the motifs needlepointers stitch most.',
        intro: 'Needlepoint covers stiff canvas with wool or cotton thread, one stitch at a time, for cushions, belts, key fobs and wall pieces. A chart puts one square on the canvas for each stitch. These nine cover a lemon cushion, a bargello flame and a key fob. Each comes as a colour chart, a symbol chart, or a hand-coloured sheet. Type a different motif and the same canvas grid carries it.',
        useCases: [
            'a cushion front',
            'a key fob',
            'a belt or strap panel',
            'a wall piece with a motif',
        ],
        faq: [
            {q: 'What is bargello?', a: 'A needlepoint style in which long stitches rise and fall in waves of graded colour, as in the flame chart.'},
            {q: 'Does the chart show which stitch to use?', a: 'Each square is one stitch on the canvas, so the chart shows where each colour goes.'},
            {q: 'Which chart is for black and white?', a: 'The symbol chart, where each square carries a black symbol.'},
        ],
    },
}
